import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { GroupService } from '@/api/services/groupService';
import {
  GroupListItem,
  GroupMember,
  SearchParams,
  GroupInfo
} from '@/api/types';
import { useChatStore } from './useChatStore';

interface GroupState {
  // 群组列表数据 - 分为管理的群组和加入的群组
  managingGroups: GroupListItem[];
  joinedGroups: GroupListItem[];
  groupMap: Map<string, GroupListItem>; // 用于快速查找的映射表（按 conversation_id）
  groupListLoading: boolean;
  groupListError: string;

  // 群组详细信息映射表
  groupInfoMap: Map<string, GroupInfo>; // 用于存储详细群组数据（按 groupId）


  // 操作方法
  getGroupList: () => Promise<Map<string, GroupListItem>>;
  getGroupMembers: (groupId: string, params?: SearchParams) => Promise<void>;
  refreshGroupMembers: (groupId: string) => Promise<void>;
  clearErrors: () => void;

  // 通过 groupId 或 conversation_id 获取群组信息
  getGroupInfo: ({ groupId, conversationId }: { groupId?: string, conversationId?: string }) => GroupInfo | null;

  // 本地状态更新方法
  addGroupToManaging: (group: GroupListItem) => void;
  addGroupToJoined: (group: GroupListItem) => void;
  removeGroupFromList: (groupId: string) => void;
  updateGroupInList: (groupId: string, updates: Partial<GroupListItem>) => void;
  updateMemberInGroup: (groupId: string, memberList: GroupMember[]) => void;
  addMemberInGroup: (groupId: string, member: GroupMember) => void;
  removeMemberInGroup: (groupId: string, memberId: string) => void;

  // 群组详细信息管理方法
  setGroupInfo: (groupId: string, groupInfo: GroupInfo) => void;
  updateGroupInfo: (groupId: string, updates: Partial<GroupInfo>) => void;
  removeGroupInfo: (groupId: string) => void;
  getGroupInfoById: (groupId: string) => GroupInfo | undefined;
}

/**
 * 群组数据管理 Store
 * 管理群组列表和成员列表的状态
 */
export const useGroupStore = create<GroupState>()(
  subscribeWithSelector((set, get) => ({
    // 初始状态
    managingGroups: [],
    joinedGroups: [],
    groupMap: new Map<string, GroupListItem>(),
    groupListLoading: false,
    groupListError: '',

    groupInfoMap: new Map<string, GroupInfo>(),

    currentGroupMembers: {},
    groupMembersLoading: {},
    groupMembersError: {},

    /**
     * 获取群组列表
     */
    getGroupList: async (): Promise<Map<string, GroupListItem>> => {
      set({ groupListLoading: true, groupListError: '' });

      try {
        const result = await GroupService.getGroupList();
        const allGroups = [...(result.managing || []), ...(result.joined || [])];
        const groupMap = new Map(allGroups.map(group => [group.conversation_id, group]));
        set({
          managingGroups: result.managing || [],
          joinedGroups: result.joined || [],
          groupMap,
          groupListLoading: false
        });
        return groupMap;
      } catch (error: any) {
        console.error('获取群组列表失败:', error);
        set({
          groupListError: error?.err_msg || '获取群组列表失败',
          groupListLoading: false
        });
        return new Map<string, GroupListItem>();
      }
    },

    /**
     * 获取群组成员列表
     * @param groupId 群组ID
     * @param params 搜索参数
     */
    getGroupMembers: async (groupId: string, params?: SearchParams) => {
      try {
        const members = await GroupService.getParticipantList(groupId, params);
        get().updateMemberInGroup(groupId, members);
      } catch (error: any) {
        console.error('获取群组成员失败:', error);
      }
    },

    /**
     * 刷新群组成员列表
     * @param groupId 群组ID
     */
    refreshGroupMembers: async (groupId: string) => {
      await get().getGroupMembers(groupId);
    },

    /**
     * 清除所有错误信息
     */
    clearErrors: () => {
      set({
        groupListError: '',
      });
    },


    /**
     * 通过 groupId 或 conversation_id 获取群组信息
     * @param groupId 群组ID
     * @param conversationId 会话ID
     * @returns 群组信息
     */
    getGroupInfo: ({ groupId, conversationId }: { groupId?: string, conversationId?: string }): GroupInfo | null => {
      try {
        // 如果传了 conversationId，则先从 groupMap 中获取 groupId
        if (conversationId) {
          const groupItem = get().groupMap.get(conversationId);
          if (groupItem) {
            groupId = groupItem.id;
          }
        }

        const groupInfo = get().groupInfoMap.get(groupId ?? '') ?? null;
        return groupInfo ?? null;
      } catch (error: any) {
        console.error('获取群组信息失败:', error);
        return null;
      }
    },

    /**
     * 添加群组到管理列表（本地更新）
     * @param group 群组信息
     */
    addGroupToManaging: (group: GroupListItem) => {
      set(state => {
        const newGroupMap = new Map(state.groupMap);
        newGroupMap.set(group.conversation_id, group);
        return {
          managingGroups: [...state.managingGroups, group],
          groupMap: newGroupMap
        };
      });
    },

    /**
     * 添加群组到加入列表（本地更新）
     * @param group 群组信息
     */
    addGroupToJoined: (group: GroupListItem) => {
      set(state => {
        const newGroupMap = new Map(state.groupMap);
        newGroupMap.set(group.conversation_id, group);
        return {
          joinedGroups: [...state.joinedGroups, group],
          groupMap: newGroupMap
        };
      });
    },

    /**
     * 从群组列表中移除（本地更新）
     * @param groupId 群组ID
     */
    removeGroupFromList: (groupId: string) => {
      set(state => {
        const newGroupMap = new Map(state.groupMap);
        const newGroupInfoMap = new Map(state.groupInfoMap);

        // 找到要删除的群组的 conversation_id
        const groupToRemove = [...state.managingGroups, ...state.joinedGroups].find(g => g.id === groupId);
        if (groupToRemove) {
          newGroupMap.delete(groupToRemove.conversation_id);
        }

        // 从群组详细信息中删除
        newGroupInfoMap.delete(groupId);

        return {
          managingGroups: state.managingGroups.filter(group => group.id !== groupId),
          joinedGroups: state.joinedGroups.filter(group => group.id !== groupId),
          groupMap: newGroupMap,
          groupInfoMap: newGroupInfoMap,
        };
      });

      // 同时移除相关的私聊会话
      const chatStore = useChatStore.getState();
      const conversationToRemove = chatStore.conversations.find((conv: any) =>
        conv.session_type === 'group' &&
        conv.participants?.some((p: any) => p.id === groupId)
      );
      if (conversationToRemove) {
        chatStore.removeConversation(conversationToRemove.id);
      }
    },

    /**
     * 更新群组列表中的群组信息（本地更新）
     * @param groupId 群组ID
     * @param updates 更新的字段
     */
    updateGroupInList: (groupId: string, updates: Partial<GroupListItem>) => {
      set(state => {
        const newGroupMap = new Map(state.groupMap);
        const newGroupInfoMap = new Map(state.groupInfoMap);

        // 找到要更新的群组
        const existingGroup = [...state.managingGroups, ...state.joinedGroups].find(g => g.id === groupId);
        if (existingGroup) {
          const updatedGroup = { ...existingGroup, ...updates };
          newGroupMap.set(updatedGroup.conversation_id, updatedGroup);
        }

        // 同时更新群组详细信息（如果存在）
        const existingGroupInfo = state.groupInfoMap.get(groupId);
        if (existingGroupInfo) {
          const updatedGroupInfo = { ...existingGroupInfo, ...updates };
          newGroupInfoMap.set(groupId, updatedGroupInfo);
        }

        return {
          managingGroups: state.managingGroups.map(group =>
            group.id === groupId
              ? { ...group, ...updates }
              : group
          ),
          joinedGroups: state.joinedGroups.map(group =>
            group.id === groupId
              ? { ...group, ...updates }
              : group
          ),
          groupMap: newGroupMap,
          groupInfoMap: newGroupInfoMap
        };
      });
    },


    /**
     * 更新群组成员信息（本地更新）
     * @param groupId 群组ID
     * @param memberId 成员ID
     * @param updates 更新的字段
     */
    updateMemberInGroup: (groupId: string, memberList: GroupMember[]) => {
      set(state => {
        const newGroupInfoMap = new Map(state.groupInfoMap);

        // 同时更新群组详细信息中的成员信息
        let existingGroupInfo = state.groupInfoMap.get(groupId);
        if (existingGroupInfo) {
          existingGroupInfo.participants = memberList;
          newGroupInfoMap.set(groupId, existingGroupInfo);
        }

        return {
          groupInfoMap: newGroupInfoMap
        };
      });
    },

    /**
     * 添加群组成员信息（本地更新）
     * @param groupId 群组ID
     * @param member 成员信息
     */
    addMemberInGroup: (groupId: string, member: GroupMember) => {
      set(state => {
        const newGroupInfoMap = new Map(state.groupInfoMap);
        const existingGroupInfo = state.groupInfoMap.get(groupId);
        if (existingGroupInfo) {
          existingGroupInfo.participants.push(member);
          newGroupInfoMap.set(groupId, existingGroupInfo);
        }
        return {
          groupInfoMap: newGroupInfoMap
        };
      });
    },

    /**
     * 移除群组成员信息（本地更新）
     * @param groupId 群组ID
     * @param memberId 成员ID
     */
    removeMemberInGroup: (groupId: string, memberId: string) => {
      set(state => {
        const newGroupInfoMap = new Map(state.groupInfoMap);
        const existingGroupInfo = state.groupInfoMap.get(groupId);
        if (existingGroupInfo) {
          existingGroupInfo.participants = existingGroupInfo.participants.filter(member => member.id !== memberId);
          newGroupInfoMap.set(groupId, existingGroupInfo);
        }
        return {
          groupInfoMap: newGroupInfoMap
        };
      });
    },

    /**
     * 设置群组详细信息
     * @param groupId 群组ID
     * @param groupInfo 群组详细信息
     */
    setGroupInfo: (groupId: string, groupInfo: GroupInfo) => {
      set(state => {
        const newGroupInfoMap = new Map(state.groupInfoMap);
        newGroupInfoMap.set(groupId, groupInfo);
        return {
          groupInfoMap: newGroupInfoMap
        };
      });
    },

    /**
     * 更新群组详细信息
     * @param groupId 群组ID
     * @param updates 更新的字段
     */
    updateGroupInfo: (groupId: string, updates: Partial<GroupInfo>) => {
      set(state => {
        const newGroupInfoMap = new Map(state.groupInfoMap);
        const existingGroupInfo = state.groupInfoMap.get(groupId);
        if (existingGroupInfo) {
          const updatedGroupInfo = { ...existingGroupInfo, ...updates };
          newGroupInfoMap.set(groupId, updatedGroupInfo);
        }
        return {
          groupInfoMap: newGroupInfoMap
        };
      });
    },

    /**
     * 移除群组详细信息
     * @param groupId 群组ID
     */
    removeGroupInfo: (groupId: string) => {
      set(state => {
        const newGroupInfoMap = new Map(state.groupInfoMap);
        newGroupInfoMap.delete(groupId);
        return {
          groupInfoMap: newGroupInfoMap
        };
      });
    },

    /**
     * 通过群组ID获取群组详细信息
     * @param groupId 群组ID
     * @returns 群组详细信息或undefined
     */
    getGroupInfoById: (groupId: string) => {
      return get().groupInfoMap.get(groupId);
    }
  }))
);
